<template>
  <div id="select" :class="{ disabled: disabled }">
    <label :class="{ active: firstOpen }" @click="openSelectOnce" for="select"
      >Games</label
    >
    <transition name="slide-fade">
      <div v-show="firstOpen" class="treeselect-container">
        <TreeSelect
          name="select"
          :modelValue="localValue"
          :multiple="true"
          :clearable="true"
          :searchable="true"
          :options="options"
          placeholder="Type to search"
          value-consists-of="LEAF_PRIORITY"
          showCountOf="LEAF_DESCENDANTS"
          :maxHeight="150"
          :showCount="true"
          :open-on-focus="true"
          @update:model-value="(e) => onChange(e)"
          :disabled="disabled"
          :load-options="loadOptions"
          :autoLoadRootOptions="true"
          :key="key"
        >
          <template #option-label="{ node, shouldShowCount, count }">
            <div class="vue-treeselect__label">
              {{ node.label }}
              <span v-show="shouldShowCount" class="treeselect__count"
                >({{ count }})</span
              >
            </div>
          </template>
        </TreeSelect>
      </div>
    </transition>
  </div>
</template>

<script>
import TreeSelect from "@r2rka/vue3-treeselect";
import "./vue-treeselect.css";
const Emitter = require("tiny-emitter");
const emitter = new Emitter();
import { mapActions, mapGetters } from "vuex";

export default {
  components: { TreeSelect },
  emits: ["update:value"],
  props: {
    edit: {
      type: Boolean
    },
    value: {
      type: [Array, null],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.setupInputPlaceholder();
    this.setupArrowIcons();
  },
  watch: {
    edit: {
      handler(newVal) {
        this.key = String(newVal);
      }
    },
    value: {
      handler(newVal) {
        this.setupInputPlaceholder();
        this.localValue = newVal;
        if (newVal.length > 0) {
          this.firstOpen = true;
        }
      },
    },
  },
  data() {
    return {
      localValue: [],
      firstOpen: false,
      options: null,
      categories: ["all"],
      key: ""
    };
  },
  computed: {
    ...mapGetters({
      getGamesCategory: "gamesCategory/getGamesCategory",
    }),
  },
  methods: {
    ...mapActions({
      actionGetGamesCategoryList: "gamesCategory/actionGetGamesCategoryList",
    }),
    openSelectOnce() {
      if (!this.firstOpen) {
        this.firstOpen = true;
      }
    },
    setupInputPlaceholder() {
      this.$nextTick(() => {
        const input = this.$el.querySelector(".vue-treeselect__input");
        if (input) {
          input.setAttribute("placeholder", "Type to search");
        }
      });
    },
    setupArrowIcons() {
      const arrowIconObserver = new MutationObserver(() => {
        const arrowDownSvgs = this.$el?.querySelectorAll(
          ".vue-treeselect__option-arrow"
        );
        if (arrowDownSvgs.length) {
          arrowDownSvgs.forEach((svg) => {
            svg.setAttribute("viewBox", "0 0 24 24");
            const svgPath = svg.querySelector("path");
            if (svgPath) {
              svgPath.setAttribute(
                "d",
                "M16.59 8.59L12 13.17L7.41 8.59L6 10L12 16L18 10L16.59 8.59Z"
              );
              svgPath.setAttribute("fill", "#121212");
            }
          });
        }
      });

      arrowIconObserver.observe(this.$el, {
        childList: true,
        subtree: true,
      });

      // Clean up observer when component is destroyed
      emitter.once("hook:beforeDestroy", () => {
        arrowIconObserver.disconnect();
      });
    },
    transformResponseGamesCategoryToOptions(games) {
      const result = [
        {
          id: "all",
          label: "All games",
          children: [],
        },
      ];

      const categories = {};

      games.forEach((game) => {
        const unknownCategory = "Unknown";
        const { slug, name, meta } = game;
        const category = meta?.primaryCategory ?? unknownCategory;

        if (!categories[category]) {
          this.categories.push(category);
          categories[category] = {
            id: category,
            label: category.charAt(0).toUpperCase() + category.slice(1),
            children: [],
          };
        }
        categories[category].children.push({ id: slug, label: name });
      });
      result[0].children.push(...Object.values(categories));

      return result;
    },
    onChange(value) {
      const selectedGames = value.filter(
        (node) => !this.categories.includes(node)
      );
      this.$emit("update:value", selectedGames);
    },
    async loadOptions({ action, callback }) {
      if (action === "LOAD_ROOT_OPTIONS") {
        await this.actionGetGamesCategoryList(this.getEnv)
          .then((response) => {
            this.options =
              this.transformResponseGamesCategoryToOptions(response.data);
            callback();
          })
          .catch((err) => {
            callback(new Error("Failed to load options: " + err));

            this.$q.notify({ type: "negative", message: err });
          });
      }
    },
  },
};
</script>

<style>
#select {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}

#select.disabled {
  border-style: dashed;
}

#select > label {
  display: block;
  text-align: left;
  padding: 10px 10px;
  color: rgba(103, 103, 103, 1);
  font-size: 16px;
  transition: font-size 0.5s ease, color 0.5s ease, line-height 0.5s ease,
    background-position 0.4s ease, background-image 0.5s ease;
  transform-origin: top left;
  line-height: 24px;
  background-image: url("down.svg");
  background-repeat: no-repeat;
  background-position-y: center;
  background-position-x: calc(100% - 7px);
  background-size: 16px 16px;
}

#select label.active {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
  padding: 10px 10px 0;
  line-height: 18px;
  background-position-y: calc(100% + 16px);
  background-position-x: calc(100% - 7px);
}

.treeselect-container {
  padding-top: 0px;
}

#select:hover,
#select:focus {
  border-color: rgba(103, 103, 103, 0.5);
}

.vue-treeselect__control {
  padding-bottom: 5px;
}
.vue-treeselect--disabled .vue-treeselect__control {
  background-color: white;
}

.vue-treeselect__control,
.vue-treeselect:not(.vue-treeselect--disabled):not(.vue-treeselect--focused)
  .vue-treeselect__control:hover,
.vue-treeselect--focused .vue-treeselect__control,
.vue-treeselect__placeholder {
  border: none;
  padding-left: 10px;
}

.vue-treeselect__control-arrow {
  display: none;
}

.vue-treeselect__control-arrow-container:before {
  content: "";
  display: inline-block;
  width: 16px;
  height: 16px;
  background-image: url("down.svg");
  background-size: contain;
  background-repeat: no-repeat;
  transition: all 0.5s ease;
  -webkit-transition: all 0.5s ease;
}

.vue-treeselect--open .vue-treeselect__control-arrow-container:before {
  transform: rotate(180deg);
  -webkit-transform: rotate(180deg);
}

.vue-treeselect--focused:not(.vue-treeselect--open) .vue-treeselect__control {
  box-shadow: none;
  border: none;
}

.vue-treeselect__option-arrow,
.vue-treeselect__option-arrow-container,
.vue-treeselect__option-arrow-placeholder {
  width: 16px;
  height: 16px;
  color: #121212;
  transform: rotateZ(360deg);
  -webkit-transform: rotateZ(360deg);
}

.vue-treeselect__option-arrow--rotated {
  transform: rotateZ(180deg) !important;
  -webkit-transform: rotateZ(180deg) !important;
}

.vue-treeselect__x,
.vue-treeselect__value-remove svg,
.vue-treeselect__option-arrow,
.vue-treeselect__checkbox--unchecked,
.vue-treeselect__label,
.vue-treeselect__multi-value-item {
  color: rgba(18, 18, 18, 1);
}

.vue-treeselect__multi-value-item {
  background: rgba(245, 245, 245, 1);
  font-size: 14px;
  line-height: 21px;
  padding: 2px 8px;
  height: 23px;
}

.vue-treeselect__value-remove {
  border-left: none;
}

.vue-treeselect__checkbox--indeterminate,
.vue-treeselect__checkbox--checked {
  border-color: rgba(18, 18, 18, 1);
  background: rgba(18, 18, 18, 1);
}

.vue-treeselect__label-container:hover .vue-treeselect__checkbox--checked,
.vue-treeselect__label-container:hover
  .vue-treeselect__checkbox--indeterminate {
  border-color: rgba(18, 18, 18, 0.7);
  background: rgba(18, 18, 18, 0.7);
}

.vue-treeselect__label-container:hover .vue-treeselect__checkbox--unchecked {
  border-color: rgba(18, 18, 18, 0.7);
}

.vue-treeselect--open .vue-treeselect__control {
  border-color: rgba(103, 103, 103, 1);
}

.vue-treeselect__list-item,
.vue-treeselect__option--highlight {
  background-color: white;
}

.vue-treeselect--open-below:not(.vue-treeselect--append-to-body)
  .vue-treeselect__menu-container {
  top: calc(100% + 2px);
}

.vue-treeselect--open-below .vue-treeselect__menu {
  border-radius: 8px;
  border: none;
  box-shadow: 0px 20px 26px 0px rgba(0, 0, 0, 0.15);
}

.vue-treeselect--open.vue-treeselect--open-below .vue-treeselect__control {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.vue-treeselect__menu {
  overflow-y: auto;
  scrollbar-width: 9px;
  scrollbar-color: rgba(213, 213, 213, 1) transparent;
}
.vue-treeselect__menu::-webkit-scrollbar {
  width: 12px;
}

.vue-treeselect__menu::-webkit-scrollbar-track {
  background: #ffffff;
}

.vue-treeselect__menu::-webkit-scrollbar-thumb {
  background-color: rgba(213, 213, 213, 1);
  border-radius: 6px;
  border: 1px solid #ffffff;
}

.vue-treeselect__checkbox,
.vue-treeselect__checkbox-container {
  width: 20px;
  height: 20px;
  min-width: 20px;
}

.vue-treeselect__check-mark,
.vue-treeselect__minus-mark {
  left: 3px;
  top: 3px;
  width: 12px;
  height: 12px;
  background-size: 12px 12px;
}

.vue-treeselect__minus-mark {
  background-image: url("minus.svg") !important;
}

.vue-treeselect__check-mark {
  background-image: url("check.svg") !important;
}

.vue-treeselect__input-container,
.vue-treeselect__input {
  width: 100%;
  padding: 5px 0;
}

.vue-treeselect:not(.vue-treeselect--has-value)
  .vue-treeselect__input::placeholder {
  opacity: 0;
}

.vue-treeselect--has-value .vue-treeselect__input::placeholder {
  opacity: 1;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
  -webkit-transition: all 0.5s ease;
}
.slide-fade-enter {
  opacity: 0;
  transform: translateY(0px);
  -webkit-transform: translateY(0px);
}
.slide-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
  -webkit-transform: translateY(0);
}
</style>
